/**
 * /share/dashboard/:id?t=<token> — the public, read-only view of a dashboard.
 * No login: the backend validates the token and that the owner left link
 * sharing on. Renders the same DashboardRenderer as the studio, brand-themed.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { coworkApi, type PublicDashboard } from '@/cowork/omniad/api/coworkApi';
import { deriveTheme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { DashboardRenderer } from './DashboardRenderer';
import type { Dataset } from './spec';

export function PublicDashboardPage() {
  const { id = '' } = useParams<{ id: string }>();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('t') || '', []);
  const [state, setState] = useState<{ status: 'loading' } | { status: 'error'; message: string } | { status: 'ok'; d: PublicDashboard }>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    if (!id || !token) { setState({ status: 'error', message: 'This link is incomplete.' }); return; }
    coworkApi.dashboards.public(id, token)
      .then((d) => { if (!cancelled) setState({ status: 'ok', d }); })
      .catch((e: Error) => { if (!cancelled) setState({ status: 'error', message: e.message }); });
    return () => { cancelled = true; };
  }, [id, token]);
  const brand = state.status === 'ok' ? (state.d.brand as any) : null;
  const theme = useMemo(() => deriveTheme(brand ?? {}), [brand]);
  useEffect(() => { if (state.status === 'ok') document.title = `${state.d.title} · Beatly`; }, [state]);

  if (state.status === 'loading') return <div className="flex min-h-screen items-center justify-center bg-[#f8f9fc] text-[13px] text-[#98a2b3]"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>;
  if (state.status === 'error') return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f9fc] px-6">
      <div className="max-w-md rounded-2xl border border-[#eaecf0] bg-white p-8 text-center shadow-sm">
        <Lock className="mx-auto h-6 w-6 text-[#98a2b3]" />
        <div className="mt-3 text-[16px] font-semibold text-[#101828]">This dashboard is not available</div>
        <div className="mt-1.5 text-[13px] text-[#667085]">{state.message}</div>
        <div className="mt-4 text-[11.5px] text-[#98a2b3]">Ask whoever sent you the link to turn link sharing back on in Beatly.</div>
      </div>
    </div>
  );
  const d = state.d;
  const logoUrl = brand?.brand?.logoUrl || brand?.logoUrl || theme.logoUrl || null;
  const brandName = brand?.brand?.name || brand?.name || theme.brandName;
  const refreshed = d.lastRefreshedAt ? new Date(d.lastRefreshedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;
  const currency = (() => { for (const ds of Object.values(d.data || {})) { const c = (ds as any)?.meta?.currency; if (typeof c === 'string' && c) return c; } return 'SEK'; })();
  return (
    <div className="min-h-screen bg-[#f8f9fc]">
      <div className="flex items-center gap-3 border-b border-[#eaecf0] bg-white px-5 py-2.5 text-[12px] text-[#667085]">
        <span className="rounded-full bg-[#f4ecff] px-2 py-0.5 text-[11px] font-semibold text-[#7c3aed]">Shared view</span>
        <span className="min-w-0 flex-1 truncate">{brandName ? `${brandName} · ` : ''}{d.title}{refreshed ? ` · data as of ${refreshed}` : ''}</span>
        <a href="https://beatly.com" target="_blank" rel="noreferrer" className="font-semibold text-[#101828] hover:underline">Made with Beatly</a>
      </div>
      {d.spec ? (
        <DashboardRenderer spec={d.spec} data={(d.data || null) as Record<string, Dataset> | null} theme={theme} brandName={brandName} logoUrl={logoUrl} refreshedAt={d.lastRefreshedAt} editable={false} currency={currency} />
      ) : d.hasHtml ? (
        <iframe title={d.title} src={coworkApi.dashboards.embedUrl(d.id, token)} className="h-[calc(100vh-44px)] w-full border-0 bg-white" />
      ) : (
        <div className="px-6 py-16 text-center text-[13px] text-[#98a2b3]">This dashboard has nothing to show yet.</div>
      )}
    </div>
  );
}
